import { useContext, useEffect, useState } from 'react';
import Debug from 'debug';
import { EnvironmentHelper } from '@bblabs/module-library/classes';
import { type LivefireCardType } from '@bblabs/module-library/constants';
import { UserStatus, EnvironmentContext } from '../components/App';
import { OutlookLivefireApi, VideoMetadata } from '../lib/OutlookLivefireApi';
import { getAuthenticatedApiService } from '../services/AuthenticatedApi';
import { userService } from '../services/UserService';
import { eventService } from '../services/EventService';

declare global {
  // extend global EventTarget to allow CustomEvents
  interface EventTarget {
    addEventListener<T extends CustomEvent>(
      type: 'insertVideo' | 'video_saved',
      callback: (event: T) => void,
    ): void;
  }
}

const cardTypes: LivefireCardType[] = ['insert_video', 'video_saved'];

const debug = Debug('o365:useLivefireApi');

export const livefireApi = new OutlookLivefireApi({
  cardTypes,
  // subscribe to the "events" and "default" feeds for this instance of the LivefireApi
  feeds: ['default', 'events'],
});

type UseLivefireApiOptions = {
  userState: UserStatus;
};

/**
 * Connect up our livefire socket for the events like insert_video.
 */
export function useLivefireApi({ userState }: UseLivefireApiOptions) {
  const env = useContext(EnvironmentContext);
  const [setup, setSetup] = useState(false);

  useEffect(() => {
    // use emitter directly so we can bypass event type checking...
    debug('setup insertVideo listener');
    livefireApi.emitter.addEventListener('insertVideo', (event: CustomEvent<VideoMetadata>) => {
      debug('received insertVideo from livefireApi');
      eventService.emit('insertVideo', event.detail);
    });

    livefireApi.emitter.addEventListener('videoSaved', () => {
      debug('received videoSaved from livefireApi');
      eventService.emit('videoSaved');
    });

    return () => {
      debug('disconnect livefire API');
      livefireApi.clientId = null;
      livefireApi.token = null;
      livefireApi.disconnect();
      setSetup(false);
    };
  }, []);

  useEffect(() => {
    const isLivefireDisconnected = livefireApi.socket === null || livefireApi.socket?.connected === false;
    debug('setup livefire start', env);
    EnvironmentHelper.setEnvironment(env);
    livefireApi.setApiService(getAuthenticatedApiService(env, userService));

    if (!setup && userState.loggedin && userState.user && userState.token && isLivefireDisconnected) {
      debug('setup livefire API');
      const clientId = userState.user.get('clientId');
      livefireApi.clientId = clientId;
      livefireApi.token = userState.token.getAccessToken();
      livefireApi.connect();
      setSetup(true);
    }
  }, [userState, env, setup]);
}
